/**
 * React Query hooks for AP (T17) — payment runs, vendor payment methods,
 * AP reports, 1099 center, bill duplicate detection.
 */
import { keepPreviousData, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  paymentRunApi,
  vendorPaymentMethodApi,
  apReportsApi,
  payablesApi,
  payablesExportApi,
  billDuplicateCheckApi,
  type ExportPreviewBody,
  type ExportFileBody,
  type MarkPaidBody,
  type ListPaymentRunsParams,
  type SelectBillsParams,
  type AddBillEntry,
  type MarkDeliveredRequest,
  type ApAgingParams,
  type ApVendorPayablesSummaryParams,
  type GrniAgingParams,
  type CashRequirementsParams,
  type PaymentsDueParams,
  type Form1099Params,
  type Generate1099Request,
  type DuplicateCheckParams,
  type RecallRequest,
  type ConfirmRecallRequest,
  type RecordReversalRequest,
} from '../services/apApi';
import type {
  PaymentRun,
  PaymentRunCreate,
  PaymentRunUpdate,
  VendorPaymentMethodCreate,
  VendorPaymentMethodUpdate,
} from '../types/ap';
import { PAYMENTS_QUERY_KEY } from './usePayments';
import { BILLS_QUERY_KEY } from './useBills';
import { ACCOUNTS_QUERY_KEY } from './useAccounts';

export const PAYMENT_RUNS_QUERY_KEY = 'payment-runs';
export const VENDOR_PAYMENT_METHODS_QUERY_KEY = 'vendor-payment-methods';
export const AP_AGING_QUERY_KEY = 'ap-aging';
export const GRNI_AGING_QUERY_KEY = 'grni-aging';
export const AP_VENDOR_PAYABLES_QUERY_KEY = 'ap-vendor-payables';
export const CASH_REQUIREMENTS_QUERY_KEY = 'cash-requirements';
export const PAYMENTS_DUE_QUERY_KEY = 'payments-due';
export const FORM_1099_QUERY_KEY = 'form-1099';
export const BILL_DUPLICATE_CHECK_QUERY_KEY = 'bill-duplicate-check';

// ============== Payment Runs ==============

export function usePaymentRuns(
  params?: ListPaymentRunsParams,
  // A caller whose params are not known yet (the handoff dialog resolves the
  // run's company from the selection) opts out rather than firing an unscoped
  // tenant-wide read whose answer it would discard.
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: [PAYMENT_RUNS_QUERY_KEY, params],
    // The list is server-paged, so every page change is a new query key. Keep the
    // current page on screen while the next one loads instead of blanking the
    // table (ui-list-pages.md §2.4).
    placeholderData: keepPreviousData,
    queryFn: () => paymentRunApi.list(params),
    enabled: options?.enabled ?? true,
  });
}

export function usePaymentRun(id: string) {
  return useQuery({
    queryKey: [PAYMENT_RUNS_QUERY_KEY, id],
    queryFn: () => paymentRunApi.get(id),
    enabled: !!id,
  });
}

export function useCreatePaymentRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: PaymentRunCreate) => paymentRunApi.create(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [PAYMENT_RUNS_QUERY_KEY] });
    },
  });
}

export function useUpdatePaymentRun(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: PaymentRunUpdate) => paymentRunApi.update(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [PAYMENT_RUNS_QUERY_KEY] });
    },
  });
}

export function useSelectableBills(params: SelectBillsParams | null) {
  return useQuery({
    queryKey: [PAYMENT_RUNS_QUERY_KEY, 'select-bills', params],
    queryFn: () => paymentRunApi.selectBills(params!),
    enabled: !!params?.run_id,
  });
}

export function useAddBillsToPaymentRun(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (bills: AddBillEntry[]) => paymentRunApi.addBills(id, bills),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [PAYMENT_RUNS_QUERY_KEY, id] });
      qc.invalidateQueries({ queryKey: [PAYMENT_RUNS_QUERY_KEY] });
    },
  });
}

/**
 * Target of the Payments Due → payment-run handoff: an existing draft run, or a
 * new one to create first.
 */
export type PaymentRunAttachTarget =
  | { kind: 'existing'; runId: string }
  | { kind: 'new'; data: PaymentRunCreate };

export interface AttachBillsToPaymentRunInput {
  target: PaymentRunAttachTarget;
  billIds: string[];
  /**
   * Called the moment a NEW run exists, BEFORE the bills are priced and
   * attached — so a caller learns about the run even when a later step throws.
   * The run is real and persisted by then, and there is no delete endpoint for
   * one, so a caller that only heard about it `onSuccess` would leave an empty
   * draft nobody knows about and mint another on every retry.
   */
  onRunCreated?: (run: PaymentRun) => void;
}

export interface AttachBillsToPaymentRunResult {
  runId: string;
  addedCount: number;
  /**
   * Named bills another OPEN run already holds — the exclusive claim the partial
   * unique index enforces. Reported separately from `unavailableCount` because
   * the clerk's next move differs: open that run, versus find out why the bill
   * stopped being payable.
   */
  claimedCount: number;
  /**
   * Named bills the run's own eligibility preview did not return at all — on
   * payment hold, no longer `due`, or moved out of the run's company between the
   * worklist read and this submit. Either way the run is short, and saying so is
   * the point.
   */
  unavailableCount: number;
}

/**
 * Create-or-reuse a draft run and attach a named set of bills to it, in one
 * mutation so the whole handoff shares the global error toast and one
 * invalidation.
 *
 * The middle step is deliberate: the worklist row carries no early-payment
 * discount and no open-run claim, so the run's OWN `select-bills` preview
 * (narrowed to these ids) is what prices each bill — sending `amount_due` with a
 * zero discount would forfeit every discount the AP clerk came for, silently.
 * Bills the preview does not return, or returns claimed, are reported as dropped
 * rather than sent: one refused bill fails the whole attach.
 */
export function useAttachBillsToPaymentRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      target,
      billIds,
      onRunCreated,
    }: AttachBillsToPaymentRunInput): Promise<AttachBillsToPaymentRunResult> => {
      let runId: string;
      if (target.kind === 'existing') {
        runId = target.runId;
      } else {
        const created = (await paymentRunApi.create(target.data)).data;
        runId = created.id;
        // Announce it before anything else can throw, and refresh the run list
        // in the same breath so the new draft is on offer as a retry target.
        qc.invalidateQueries({ queryKey: [PAYMENT_RUNS_QUERY_KEY] });
        onRunCreated?.(created);
      }

      const preview = await paymentRunApi.selectBills({
        run_id: runId,
        bill_ids: billIds,
      });
      const attachable = preview.data.filter((b) => !b.claimed_by_run_id);
      if (attachable.length > 0) {
        await paymentRunApi.addBills(
          runId,
          attachable.map((b) => ({
            bill_id: b.bill_id,
            // pay_amount is the net cash to remit (amount_due − discount taken);
            // sending amount_due here plus the discount would exceed amount_due
            // and the backend rejects the whole attach.
            amount_to_pay: b.pay_amount,
            discount_taken: b.discount_available,
          })),
        );
      }
      const claimedCount = preview.data.length - attachable.length;
      return {
        runId,
        addedCount: attachable.length,
        claimedCount,
        unavailableCount: billIds.length - preview.data.length,
      };
    },
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: [PAYMENT_RUNS_QUERY_KEY, result.runId] });
      qc.invalidateQueries({ queryKey: [PAYMENT_RUNS_QUERY_KEY] });
      qc.invalidateQueries({ queryKey: [PAYMENTS_DUE_QUERY_KEY] });
      qc.invalidateQueries({ queryKey: [BILLS_QUERY_KEY] });
    },
  });
}

export function useRemoveBillFromPaymentRun(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (billId: string) => paymentRunApi.removeBill(id, billId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [PAYMENT_RUNS_QUERY_KEY, id] });
    },
  });
}

export function useSubmitPaymentRun(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => paymentRunApi.submit(id),
    onSuccess: () => {
      // A below-threshold submit auto-approves and emits payments/JE, so
      // invalidate the same caches as approve (payments, bills, accounts).
      qc.invalidateQueries({ queryKey: [PAYMENT_RUNS_QUERY_KEY] });
      qc.invalidateQueries({ queryKey: [PAYMENTS_QUERY_KEY] });
      qc.invalidateQueries({ queryKey: [BILLS_QUERY_KEY] });
      qc.invalidateQueries({ queryKey: [ACCOUNTS_QUERY_KEY] });
    },
  });
}

export function useApprovePaymentRun(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => paymentRunApi.approve(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [PAYMENT_RUNS_QUERY_KEY] });
      qc.invalidateQueries({ queryKey: [PAYMENTS_QUERY_KEY] });
      qc.invalidateQueries({ queryKey: [BILLS_QUERY_KEY] });
      qc.invalidateQueries({ queryKey: [ACCOUNTS_QUERY_KEY] });
    },
  });
}

export function useMarkPaymentRunDelivered(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data?: MarkDeliveredRequest) =>
      paymentRunApi.markDelivered(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [PAYMENT_RUNS_QUERY_KEY] });
    },
  });
}

export function useCompletePaymentRun(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => paymentRunApi.complete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [PAYMENT_RUNS_QUERY_KEY] });
    },
  });
}

// Invalidate the caches every reversal action touches: runs (status),
// payments (status), bills (balances), accounts (ledger).
function useReversalInvalidation() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: [PAYMENT_RUNS_QUERY_KEY] });
    qc.invalidateQueries({ queryKey: [PAYMENTS_QUERY_KEY] });
    qc.invalidateQueries({ queryKey: [BILLS_QUERY_KEY] });
    qc.invalidateQueries({ queryKey: [ACCOUNTS_QUERY_KEY] });
  };
}

export function useVoidPaymentRun(id: string) {
  const invalidate = useReversalInvalidation();
  return useMutation({
    mutationFn: (reason: string) => paymentRunApi.void(id, reason),
    onSuccess: invalidate,
  });
}

export function useStopPaymentRun(id: string) {
  const invalidate = useReversalInvalidation();
  return useMutation({
    mutationFn: (reason: string) => paymentRunApi.stopPayment(id, reason),
    onSuccess: invalidate,
  });
}

export function useRecallPaymentRun(id: string) {
  const invalidate = useReversalInvalidation();
  return useMutation({
    mutationFn: (data: RecallRequest) => paymentRunApi.recall(id, data),
    onSuccess: invalidate,
  });
}

export function useConfirmRecall(id: string) {
  const invalidate = useReversalInvalidation();
  return useMutation({
    mutationFn: (data: ConfirmRecallRequest) =>
      paymentRunApi.confirmRecall(id, data),
    onSuccess: invalidate,
  });
}

export function useRejectRecall(id: string) {
  const invalidate = useReversalInvalidation();
  return useMutation({
    mutationFn: (reason: string) => paymentRunApi.rejectRecall(id, reason),
    onSuccess: invalidate,
  });
}

export function useRecordReversal(id: string) {
  const invalidate = useReversalInvalidation();
  return useMutation({
    mutationFn: (data: RecordReversalRequest) =>
      paymentRunApi.recordReversal(id, data),
    onSuccess: invalidate,
  });
}

export function usePaymentRunReversals(id: string) {
  return useQuery({
    queryKey: [PAYMENT_RUNS_QUERY_KEY, id, 'reversals'],
    queryFn: () => paymentRunApi.listReversals(id),
    enabled: !!id,
  });
}

export function useAchReturnCodes() {
  return useQuery({
    queryKey: ['ach-return-codes'],
    queryFn: () => paymentRunApi.achReturnCodes(),
    staleTime: Infinity,
  });
}

// ============== Vendor Payment Methods ==============

export function useVendorPaymentMethods(vendorId: string) {
  return useQuery({
    queryKey: [VENDOR_PAYMENT_METHODS_QUERY_KEY, vendorId],
    queryFn: () => vendorPaymentMethodApi.list(vendorId),
    enabled: !!vendorId,
  });
}

export function useCreateVendorPaymentMethod(vendorId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: VendorPaymentMethodCreate) =>
      vendorPaymentMethodApi.create(vendorId, data),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: [VENDOR_PAYMENT_METHODS_QUERY_KEY, vendorId],
      });
    },
  });
}

export function useUpdateVendorPaymentMethod(vendorId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      methodId,
      data,
    }: {
      methodId: string;
      data: VendorPaymentMethodUpdate;
    }) => vendorPaymentMethodApi.update(vendorId, methodId, data),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: [VENDOR_PAYMENT_METHODS_QUERY_KEY, vendorId],
      });
    },
  });
}

export function useDeleteVendorPaymentMethod(vendorId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (methodId: string) =>
      vendorPaymentMethodApi.delete(vendorId, methodId),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: [VENDOR_PAYMENT_METHODS_QUERY_KEY, vendorId],
      });
    },
  });
}

export function useSetDefaultVendorPaymentMethod(vendorId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (methodId: string) =>
      vendorPaymentMethodApi.setDefault(vendorId, methodId),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: [VENDOR_PAYMENT_METHODS_QUERY_KEY, vendorId],
      });
    },
  });
}

// ============== AP Reports ==============

export function useApAging(params?: ApAgingParams) {
  return useQuery({
    queryKey: [AP_AGING_QUERY_KEY, params],
    queryFn: () => apReportsApi.apAging(params),
  });
}

/** One vendor's open payables position — the figure the procurement vendor page
 *  shows (INB-J11). Sourced from the accounting seam, so it always agrees with
 *  AP Aging. Disabled without a vendor id; not retried, so a caller without
 *  accounting access simply gets no data instead of a retry storm. */
export function useApVendorPayablesSummary(
  vendorId: string | undefined,
  params?: ApVendorPayablesSummaryParams,
) {
  return useQuery({
    queryKey: [AP_VENDOR_PAYABLES_QUERY_KEY, vendorId, params],
    queryFn: () => apReportsApi.vendorPayablesSummary(vendorId!, params),
    enabled: !!vendorId,
    retry: false,
  });
}

export function useGrniAging(params?: GrniAgingParams) {
  return useQuery({
    queryKey: [GRNI_AGING_QUERY_KEY, params],
    queryFn: () => apReportsApi.grniAging(params),
  });
}

export function useCashRequirements(params?: CashRequirementsParams) {
  return useQuery({
    queryKey: [CASH_REQUIREMENTS_QUERY_KEY, params],
    queryFn: () => apReportsApi.cashRequirements(params),
  });
}

export function usePaymentsDue(params?: PaymentsDueParams) {
  return useQuery({
    queryKey: [PAYMENTS_DUE_QUERY_KEY, params],
    queryFn: () => payablesApi.paymentsDue(params),
  });
}

export function useForm1099(taxYear: number, params?: Form1099Params) {
  return useQuery({
    queryKey: [FORM_1099_QUERY_KEY, taxYear, params],
    queryFn: () => apReportsApi.get1099(taxYear, params),
    enabled: !!taxYear,
  });
}

export function useGenerate1099(taxYear: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Generate1099Request) =>
      apReportsApi.generate1099(taxYear, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [FORM_1099_QUERY_KEY, taxYear] });
    },
  });
}

export function useGenerate1099MagneticFile(taxYear: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Generate1099Request) =>
      apReportsApi.generate1099MagneticFile(taxYear, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [FORM_1099_QUERY_KEY, taxYear] });
    },
  });
}

// ============== Bill Duplicate Check ==============

export function useBillDuplicateCheck(
  params: DuplicateCheckParams | null,
  enabled = true,
) {
  return useQuery({
    queryKey: [BILL_DUPLICATE_CHECK_QUERY_KEY, params],
    queryFn: () => billDuplicateCheckApi.check(params!),
    enabled: enabled && !!params && params.total > 0,
  });
}

// ============== Payments-Due bank-file export ==============

export const PAYABLES_EXPORT_DEFAULTS_QUERY_KEY = 'payables-export-defaults';

export function usePaymentExportDefaults(enabled = true) {
  return useQuery({
    queryKey: [PAYABLES_EXPORT_DEFAULTS_QUERY_KEY],
    queryFn: () => payablesExportApi.getDefaults(),
    enabled,
    staleTime: 60_000,
  });
}

export function usePreviewPaymentExport() {
  return useMutation({
    mutationFn: (body: ExportPreviewBody) => payablesExportApi.preview(body),
  });
}

export function useExportPaymentFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ExportFileBody) => payablesExportApi.exportFile(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [PAYABLES_EXPORT_DEFAULTS_QUERY_KEY] });
    },
  });
}

export function useMarkExportPaid() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: MarkPaidBody) => payablesExportApi.markPaid(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [PAYMENTS_DUE_QUERY_KEY] });
      qc.invalidateQueries({ queryKey: [BILLS_QUERY_KEY] });
      qc.invalidateQueries({ queryKey: [PAYMENTS_QUERY_KEY] });
    },
  });
}
